"use client";

import { useEffect } from "react";
import Script from "next/script";

import { createClient } from "@/lib/supabase-browser";

declare global {
  interface Window {
    $crisp?: unknown[] | { push: (args: unknown[]) => void };
    CRISP_WEBSITE_ID?: string;
  }
}

function crispPush(args: unknown[]) {
  if (typeof window === "undefined") return;
  const c = window.$crisp;
  if (!c) return;
  (c as { push: (a: unknown[]) => void }).push(args);
}

export default function CrispChat() {
  const websiteId = process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID;

  useEffect(() => {
    if (!websiteId) return;
    const supabase = createClient();
    let disposed = false;

    // RFC 2606 reserved TLDs (plus common local ones). Pushing an address
    // on one of these into Crisp triggers their "you misprinted your email"
    // validation popup because the domain is intentionally non-routable —
    // we use these for seeded test accounts (e.g. pro@rekkd.test).
    const NON_ROUTABLE_TLDS = new Set([
      "test",
      "example",
      "invalid",
      "localhost",
      "local",
    ]);

    const isRoutableEmail = (email: string) => {
      const at = email.lastIndexOf("@");
      if (at < 0) return false;
      const domain = email.slice(at + 1).toLowerCase();
      const tld = domain.split(".").pop() ?? "";
      if (NON_ROUTABLE_TLDS.has(tld)) return false;
      // Reject obvious example.com / example.org / rekkd.test too.
      if (domain === "example.com" || domain === "example.org") return false;
      return true;
    };

    const apply = (user: { email?: string | null; user_metadata?: Record<string, unknown>; id?: string } | null) => {
      if (disposed) return;
      if (!user?.email) {
        crispPush(["do", "session:reset"]);
        return;
      }
      const meta = user.user_metadata ?? {};
      const name =
        (typeof meta.full_name === "string" && meta.full_name) ||
        (typeof meta.name === "string" && meta.name) ||
        user.email.split("@")[0];
      const avatar = typeof meta.avatar_url === "string" ? meta.avatar_url : null;

      // Only push the email into Crisp when the domain is actually
      // routable. Test / reserved TLDs make Crisp flag the session as a
      // typo; we still set nickname + internal user_id so the agent can
      // identify the session.
      if (isRoutableEmail(user.email)) {
        crispPush(["set", "user:email", [user.email]]);
      }
      crispPush(["set", "user:nickname", [name]]);
      if (avatar) crispPush(["set", "user:avatar", [avatar]]);
      if (user.id) {
        crispPush(["set", "session:data", [[["user_id", user.id]]]]);
      }
    };

    supabase.auth
      .getUser()
      .then(({ data }) => apply(data.user))
      .catch(() => {});

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => apply(session?.user ?? null));

    return () => {
      disposed = true;
      subscription.unsubscribe();
    };
  }, [websiteId]);

  if (!websiteId) return null;

  return (
    <Script id="crisp-chat" strategy="afterInteractive">
      {`
        window.$crisp = [];
        window.CRISP_WEBSITE_ID = ${JSON.stringify(websiteId)};
        (function(){
          var d = document;
          var s = d.createElement("script");
          s.src = "https://client.crisp.chat/l.js";
          s.async = 1;
          d.getElementsByTagName("head")[0].appendChild(s);
        })();
      `}
    </Script>
  );
}
